import type { InventorySettings } from "@redotech/redo-inventory-common/inventory-settings";
import {
  InventoryPolicy as InventoryPolicyProductModel,
  ProductStatus,
} from "@redotech/redo-product-common/product";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { InventoryPolicy as InventoryPolicyDraftReturnModel } from "@redotech/redo-shopify-order-common/shopify-inventory-policy";

export function parseInventoryPolicy(
  inventoryPolicy: string | undefined | null,
): FixMeInventoryPolicy | undefined {
  if (!inventoryPolicy) {
    return undefined;
  }
  switch (inventoryPolicy) {
    case "CONTINUE":
      return InventoryPolicyDraftReturnModel.CONTINUE;
    case "DENY":
      return InventoryPolicyDraftReturnModel.DENY;
    case "continue":
      return InventoryPolicyProductModel.CONTINUE;
    case "deny":
      return InventoryPolicyProductModel.DENY;
    default:
      throw new Error(`Unknown inventory policy: ${inventoryPolicy}`);
  }
}
// There are for some reason two enums with different capitalizations. This is a workaround so we can at least share this function.
// But we should pick one of these and backfill the database to use the same capitalization.
type FixMeInventoryPolicy =
  | InventoryPolicyDraftReturnModel
  | InventoryPolicyProductModel;

/**
 * This function is used to check if a variant's properties allow it to be "purchased" via
 * exchange.
 *
 * There are two types of InventoryPolicy enum, one in the draft return model and one in
 * the product model.
 * The product model enum is lowercase and the draft return model enum is uppercase. This
 * function only accepts the draft return model enum.
 * @param settings - The settings object.
 * @param returnType - The type of return.
 * @param availableForSale - Whether the variant is available for sale. This is populated
 *   on the ExchangeProductVariant object on the frontend, and is actually a representation
 *   of this function's return value.
 * @param inventoryPolicy - The inventory policy of the variant. Based on the draft return
 *   model enum.
 * @param inventoryTracked - Whether the variant is tracked. Essentially another ignore
 *   inventory argument, but derived from the shopify product.
 * @param inventoryQuantity - The quantity of the variant.
 * @param ignoreStock - Whether to ignore the stock.
 * @returns Whether the variant can be "purchased" via exchange.
 */
interface VariantStockOptions {
  inventorySettings: InventorySettings;
  returnType?: ReturnTypeEnum;
  inventoryPolicy?: FixMeInventoryPolicy;
  inventoryTracked?: boolean;
  inventoryQuantity?: number;
  ignoreStock?: boolean;
}

export function isVariantInStock({
  inventorySettings,
  returnType = ReturnTypeEnum.RETURN,
  inventoryPolicy = InventoryPolicyDraftReturnModel.DENY,
  inventoryTracked = true,
  inventoryQuantity = 0,
  ignoreStock = false,
}: VariantStockOptions): boolean {
  const ignoreDueToArgs = ignoreStock;

  const ignoreBecauseInventoryIsNotTracked = !inventoryTracked;

  const ignoreAccordingToTeamSettings = inventorySettings.ignoreInventory;

  const ignoreAccordingToReturnType =
    inventorySettings.ignoreInventoryForReturnTypes.includes(returnType);

  const ignoreAccordingToInventoryPolicy =
    // Even though this function should only accept the draft return model enum,
    // we are converting to lowercase to be safe. See the comment on the FixMeInventoryPolicy type.
    inventoryPolicy.toLowerCase() ===
    InventoryPolicyDraftReturnModel.CONTINUE.toLowerCase();

  const quantityAboveMinimumStock =
    (inventoryQuantity || 0) >= inventorySettings.minimum_stock;

  return (
    ignoreDueToArgs ||
    ignoreBecauseInventoryIsNotTracked ||
    ignoreAccordingToTeamSettings ||
    ignoreAccordingToReturnType ||
    ignoreAccordingToInventoryPolicy ||
    quantityAboveMinimumStock
  );
}

export function isProductVariantInStock({
  productStatus,
  ...variantStockOptions
}: VariantStockOptions & { productStatus?: string | null }): boolean {
  return (
    (productStatus == null ||
      productStatus.toLowerCase() === ProductStatus.ACTIVE) &&
    isVariantInStock(variantStockOptions)
  );
}
